import * as BABYLON from '@babylonjs/core';
import { Fixtuur, Render } from '@fixtuur/backpack';
import * as O from 'fp-ts/Option';
import { pipe } from 'fp-ts/lib/function';
import * as Event from '../event';
import { addModel, attachmentPointsLayer, emit, scene } from '../renderer';
import * as ShadowRoom from '../shadowRoom';
import * as Transform from '../transform';
const isParticleAttachment = Render.Particle.Tree.isParticleAttachment;
const FParticle = Fixtuur.Configuration.Particle;
const FConfiguration = Fixtuur.Configuration;
// Recursive function used by fromFixtuurConfiguration
const fromFixtuurConfiguration_ = (particle, particleMap, position = { x: 0, y: 0, z: 0 }, orientation = { x: 0, y: 0, z: 0, w: 1 }) => {
    const node = {
        id: FParticle.id(particle),
        attachmentTag: FParticle.attachmentTag(particle),
        position,
        orientation,
        modelUrl: FParticle.modelUrl(particle),
        originDelta: FParticle.originDelta(particle),
        attachments: {
            active: [],
            options: {},
        },
    };
    for (const slot of FParticle.slots(particle)) {
        const attachedParticle = particleMap.get(slot.particleId);
        if (!attachedParticle) {
            throw new Error(`No particle found with id ${slot.particleId}`);
        }
        const attachedNode = fromFixtuurConfiguration_(attachedParticle, particleMap, slot.position, slot.orientation);
        if (slot.optional) {
            if (!node.attachments.options[slot.name]) {
                node.attachments.options[slot.name] = [];
            }
            node.attachments.options[slot.name].push(attachedNode);
        }
        else {
            node.attachments.active.push(attachedNode);
        }
    }
    return node;
};
export const fromFixtuurConfiguration = (configuration) => {
    const particles = FConfiguration.particles(configuration);
    return pipe(FParticle.findRoot(particles), O.map(root => fromFixtuurConfiguration_(root, FConfiguration.particleMap(configuration))));
};
const addParticleModel = async (renderer, treeNode, parentTransform) => {
    // create a root node and a delta origin node for offset position + rotation
    const particleTransform = Transform.createForParticle();
    particleTransform.id = treeNode.id;
    const deltaOriginTransform = Transform.createForParticleDeltaOrigin();
    deltaOriginTransform.parent = particleTransform;
    // attach the meshes to the delta origin node
    try {
        await addModel(renderer, deltaOriginTransform, treeNode.modelUrl);
    }
    catch {
        throw new Error(`Failed to fetch 3d model with id: ${treeNode.id}`);
    }
    // parent the new particle transform if it's still valid (async operation to fetch, may have been removed!)
    if (parentTransform && !parentTransform.isDisposed()) {
        particleTransform.parent = parentTransform;
    }
    else {
        particleTransform.dispose();
        return particleTransform;
    }
    // set root node to raw position from attachment point
    let { x: px, y: py, z: pz } = treeNode.position;
    let { w: rw, x: rx, y: ry, z: rz } = treeNode.orientation;
    particleTransform.position = new BABYLON.Vector3(px, py, pz);
    particleTransform.rotationQuaternion = new BABYLON.Quaternion(rx, ry, rz, rw);
    // set delta origin to that from attached model
    let { x: opx, y: opy, z: opz } = treeNode.originDelta.position;
    let { w: orw, x: orx, y: ory, z: orz } = treeNode.originDelta.orientation;
    deltaOriginTransform.position = new BABYLON.Vector3(opx, opy, opz);
    deltaOriginTransform.rotationQuaternion = new BABYLON.Quaternion(orx, ory, orz, orw);
    // return root node
    return deltaOriginTransform;
};
const addHotspot = (apUtilLayer, parentTransform, attachmentKey, attachment) => {
    const actionManager = new BABYLON.ActionManager();
    if (isParticleAttachment(attachment)) {
        attachment.attached.forEach(childAttachment => {
            // create the animated sphere, this is shown in the UI
            const apSphere = BABYLON.CreateSphere('attachmentPoint', { diameter: 0.04 }, apUtilLayer.utilityLayerScene);
            const sphereMaterial = new BABYLON.StandardMaterial('hotspot');
            sphereMaterial.transparencyMode = 2;
            sphereMaterial.alpha = 0.1;
            const color = new BABYLON.Color3(1, 1, 1);
            sphereMaterial.emissiveColor = color;
            apSphere.material = sphereMaterial;
            apSphere.parent = parentTransform;
            let { x: px, y: py, z: pz } = childAttachment.position;
            apSphere.position = new BABYLON.Vector3(px, py, pz);
            apSphere.outlineColor = BABYLON.Color3.Black();
            apSphere.outlineWidth = 0.005;
            apSphere.renderOutline = true;
            // create a selection sphere, this is used entirely for click interaction.
            // it's larger than the gui one to aid touch interaction
            const selectionSphere = BABYLON.CreateSphere('attachmentPointSelectionSphere', { diameter: 0.1 }, apUtilLayer.utilityLayerScene);
            selectionSphere.visibility = 0;
            selectionSphere.id = `${attachmentKey}:${attachment.name}`;
            selectionSphere.parent = apSphere;
            selectionSphere.actionManager = actionManager;
            // finally, create a copy of the sphere to animate a highlight on a second after creation
            const highlightAnimationSphere = BABYLON.CreateSphere('attachmentPointHighlightAnimation', { diameter: 0.04 }, apUtilLayer.utilityLayerScene);
            highlightAnimationSphere.parent = apSphere;
            highlightAnimationSphere.material = sphereMaterial;
            const highlightAnimation = new BABYLON.Animation('pulse-animation', 'scaling', 60, BABYLON.Animation.ANIMATIONTYPE_VECTOR3, BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT);
            const keyFrames = [];
            keyFrames.push({
                frame: 0,
                value: new BABYLON.Vector3(1, 1, 1),
            });
            keyFrames.push({
                frame: 60,
                value: new BABYLON.Vector3(1, 1, 1),
            });
            keyFrames.push({
                frame: 90,
                value: new BABYLON.Vector3(3, 3, 3),
            });
            keyFrames.push({
                frame: 120,
                value: new BABYLON.Vector3(1, 1, 1),
            });
            highlightAnimation.setKeys(keyFrames);
            const easingFunction = new BABYLON.CircleEase();
            easingFunction.setEasingMode(BABYLON.EasingFunction.EASINGMODE_EASEOUT);
            highlightAnimation.setEasingFunction(easingFunction);
            highlightAnimationSphere.animations.push(highlightAnimation);
            const highlightAnimationContext = apUtilLayer.originalScene.beginAnimation(highlightAnimationSphere, 0, 120, false);
            actionManager.registerAction(new BABYLON.CombineAction(BABYLON.ActionManager.OnPointerOverTrigger, [
                new BABYLON.InterpolateValueAction(BABYLON.ActionManager.OnPointerOverTrigger, sphereMaterial, 'alpha', 0.5, 100, undefined, true),
                new BABYLON.InterpolateValueAction(BABYLON.ActionManager.OnPointerOverTrigger, apSphere, 'scaling', new BABYLON.Vector3(1.5, 1.5, 1.5), 100, undefined, true),
                new BABYLON.ExecuteCodeAction(BABYLON.ActionManager.OnPointerOverTrigger, () => {
                    highlightAnimationContext.stop();
                    highlightAnimationSphere.scaling = BABYLON.Vector3.One();
                }),
            ]));
            actionManager.registerAction(new BABYLON.CombineAction(BABYLON.ActionManager.OnPointerOutTrigger, [
                new BABYLON.InterpolateValueAction(BABYLON.ActionManager.OnPointerOutTrigger, sphereMaterial, 'alpha', 0.2, 500, undefined, true),
                new BABYLON.InterpolateValueAction(BABYLON.ActionManager.OnPointerOutTrigger, apSphere, 'scaling', new BABYLON.Vector3(1, 1, 1), 500, undefined, true),
            ]));
        });
    }
};
const addParticleTreeRecursive = async (renderer, treeNode, parentTransform) => {
    const transform = await addParticleModel(renderer, treeNode, parentTransform);
    await Promise.all(treeNode.attachments.active.flatMap(attached => {
        return addParticleTreeRecursive(renderer, attached, transform);
    }));
    pipe(O.Do, O.apS('apUtilLayer', attachmentPointsLayer(renderer)), O.map(({ apUtilLayer }) => {
        Object.entries(treeNode.attachments.options).forEach(([key, val]) => val.forEach(v => {
            addHotspot(apUtilLayer, transform, key, v);
        }));
    }));
    const transformHash = await Render.Particle.Tree.transformHash(treeNode);
    const metadata = { treeNode, transformHash };
    transform.metadata = metadata;
    return transform;
};
export const render = async (renderer, tree) => {
    const _scene = scene(renderer);
    Transform.disposeType(Transform.Type.SINGLE_PARTICLE_TRANSFORM)(_scene.transformNodes);
    const oldTransform = Transform.findRootFurniture(_scene.transformNodes);
    const oldHotSpots = pipe(renderer, attachmentPointsLayer, O.map(ap => [...ap.utilityLayerScene.meshes]), O.getOrElse(() => [])); // [...apUtilLayer.utilityLayerScene.meshes];
    const allOldTransforms = Transform.findAllRootFurniture(_scene.transformNodes);
    try {
        // create new furniture transform and hide it
        const newTransform = Transform.createForRootFurniture();
        newTransform.setEnabled(false);
        newTransform.position = new BABYLON.Vector3(0.0, 0.2, 0.0);
        await addParticleTreeRecursive(renderer, tree, newTransform);
        const back = newTransform.getHierarchyBoundingVectors().min.z;
        pipe(renderer, scene, ShadowRoom.ShadowFloor.get, O.map(g => {
            g.position = new BABYLON.Vector3(0, 0, 10 + back);
        }));
        pipe(renderer, scene, ShadowRoom.ShadowWall.get, O.map(w => {
            w.position = new BABYLON.Vector3(0, 10, back);
        }));
        // identify the new nodes and delete the old furniture
        allOldTransforms.forEach(transform => transform.dispose(false, true));
        // clear old hotspots to ensure they don't get created during a clear (async op)
        oldHotSpots.forEach(hotspot => hotspot.dispose(false, true));
        // show the new furniture and animate in the new nodes
        newTransform.setEnabled(true);
        return { newTransform };
    }
    catch (err) {
        emit(renderer, Event.Kind.OnError, [
            { reason: err?.message ?? 'Failed to add furniture' },
        ]);
        return { newTransform: null };
    }
};
